import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from 'react';
import ReactMarkdown, {} from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
import { linkClasses } from '../Link/Link.js';
/**
 * KaTeX's options, fixed here rather than exposed.
 *
 * Which of these are safety and which are ergonomics is worth keeping
 * straight: `trust` is the safety one, and the other two are about not
 * punishing a tutor for a half-typed formula.
 */
const KATEX_OPTIONS = {
    /** A broken expression is shown, in red, rather than thrown. */
    throwOnError: false,
    /**
     * No `\href`, no `\url`, no `\includegraphics`. The default, restated
     * because this is the line that would have to change to make KaTeX able to
     * emit a link, and it should never change quietly.
     */
    trust: false,
    /**
     * Unicode and other non-strict constructs pass without a console warning.
     * A tutor typing `$50°$` has done nothing that deserves developer output.
     */
    strict: 'ignore',
};
/**
 * A box that scrolls sideways because its content cannot wrap — a display
 * formula, a code block, a table — and is a Tab stop exactly while it does.
 *
 * Chrome and Firefox let the keyboard into an overflowing box by themselves;
 * Safari does not, so the end of a long formula on a phone was out of reach
 * without a pointer (WCAG 2.1.1). A box that fits takes no Tab stop, so a
 * practice full of short formulas gains none.
 */
function Scroller({ as: Tag = 'span', className, children, }) {
    const measure = useCallback((element) => {
        if (!element)
            return;
        const update = () => {
            if (element.scrollWidth > element.clientWidth + 1)
                element.tabIndex = 0;
            else
                element.removeAttribute('tabindex');
        };
        update();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        observer?.observe(element);
        return () => observer?.disconnect();
    }, []);
    return (_jsx(Tag, { ref: measure, className: cn(className, FOCUS_RING), children: children }));
}
/** Block rendering: the design system's type scale, applied to Markdown. */
const BLOCK_COMPONENTS = {
    // The practice's own headings sit below the page's, so they start small.
    h1: ({ children }) => _jsx("p", { className: "text-h4 font-semibold text-text", children: children }),
    h2: ({ children }) => _jsx("p", { className: "text-h4 font-semibold text-text", children: children }),
    h3: ({ children }) => _jsx("p", { className: "text-body font-semibold text-text", children: children }),
    h4: ({ children }) => _jsx("p", { className: "text-body font-semibold text-text", children: children }),
    h5: ({ children }) => _jsx("p", { className: "text-body font-semibold text-text", children: children }),
    h6: ({ children }) => _jsx("p", { className: "text-body font-semibold text-text", children: children }),
    p: ({ children }) => _jsx("p", { className: "text-pretty", children: children }),
    ul: ({ children }) => _jsx("ul", { className: "list-disc pl-6", children: children }),
    ol: ({ children }) => _jsx("ol", { className: "list-decimal pl-6", children: children }),
    // A long line of code cannot wrap, so it scrolls inside its own box rather
    // than widening the page. Same reasoning as the display-maths rule in
    // theme.css.
    pre: ({ children }) => (_jsx(Scroller, { as: "pre", className: "overflow-x-auto rounded-sm bg-surface-secondary p-3 text-body-sm", children: children })),
    code: ({ children, className }) => 
    // react-markdown marks a fenced block with a language class; an inline
    // span has none. The block's own styling is on the `pre` above, so this
    // only decorates the inline case.
    className === undefined ? (_jsx("code", { className: "rounded-xs bg-surface-secondary px-1 py-0.5 font-mono text-body-sm", children: children })) : (_jsx("code", { className: className, children: children })),
    // A table is wide by nature. It gets the same treatment as code and maths.
    table: ({ children }) => (_jsx(Scroller, { className: "block overflow-x-auto", children: _jsx("table", { className: "w-full border-collapse text-body-sm", children: children }) })),
    // KaTeX's own spans pass through untouched; only a display formula's box,
    // which scrolls (theme.css), is swapped for the scroller above.
    span: ({ node: _node, className, children, ...rest }) => className?.split(' ').includes('katex-display') ? (_jsx(Scroller, { className: className, children: children })) : (_jsx("span", { className: className, ...rest, children: children })),
    th: ({ children }) => (_jsx("th", { className: "border border-border px-3 py-1.5 text-left font-semibold", children: children })),
    td: ({ children }) => _jsx("td", { className: "border border-border px-3 py-1.5", children: children }),
    // Untrusted links: opened in a new tab, and told not to hand this page's
    // context to whatever is on the other end.
    a: ({ children, href }) => (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer nofollow", className: linkClasses(), children: children })),
};
/**
 * Inline rendering: the same, with paragraphs flattened.
 *
 * A `<p>` inside an option row would break out of the row's flexbox and put
 * the text on its own line. Everything else is inherited from the block set,
 * because a list or a table in an answer option is odd but not wrong.
 */
const INLINE_COMPONENTS = {
    ...BLOCK_COMPONENTS,
    p: ({ children }) => _jsx(_Fragment, { children: children }),
};
export function RichText({ children, className, inline = false }) {
    return (_jsx("span", { className: cn(
        // `display: contents` in the inline case: the wrapper has to exist to
        // carry the maths styling hooks, but it must not become a box in the
        // middle of a flex row.
        inline ? 'contents' : 'flex flex-col gap-2', className), children: _jsx(ReactMarkdown, { remarkPlugins: [remarkMath], rehypePlugins: [[rehypeKatex, KATEX_OPTIONS]], components: inline ? INLINE_COMPONENTS : BLOCK_COMPONENTS, children: children }) }));
}
//# sourceMappingURL=RichText.js.map